import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import type { IAlert } from 'common/interfaces/IAlert'
import type {
  EncounterPhotoApiItem,
  UpdateEncounterPhotoFile,
  UpdateEncounterPhotosResponse,
} from 'features/Procedure/state/procedure-api.type'
import { generateEncounterPhotoFileName } from './photos.utils'

type PhotoCategory = 'before' | 'after'

type NotificationSetter = (data: IAlert) => void

export type PreparedEncounterPhotoUpload = {
  file: File
  fileName: string
  order: number
  sourceFileName: string
}

export type EncounterPhotoUploadFailure = {
  order: number
  fileName: string
  sourceFileName: string
  description: string | string[]
}

type BuildEncounterPhotoUploadQueueArgs = {
  category: PhotoCategory
  files: File[]
  existingPhotos: EncounterPhotoApiItem[]
  overrideFileName?: string
}

type RetryEncounterPhotoUpdatesArgs = {
  execute: (files: UpdateEncounterPhotoFile[]) => Promise<UpdateEncounterPhotosResponse>
  files: UpdateEncounterPhotoFile[]
  fallbackMessage: string
}

const PHOTO_UPDATE_MAX_RETRIES = 5
const PHOTO_UPDATE_INITIAL_BACKOFF_MS = 1000
const PHOTO_UPDATE_MAX_BACKOFF_MS = 8000
const MAX_ENCOUNTER_PHOTO_FILE_SIZE_BYTES = 10 * 1024 * 1024
const ENCOUNTER_PHOTO_FILE_SIZE_MESSAGE = 'The photo must not be greater than 10240 kilobytes.'
const ENCOUNTER_PHOTO_SELECTION_READ_ERROR_MESSAGE =
  'This photo could not be read from Android Gallery/Photos. Please select it again. If it keeps failing, use the Files app instead of Gallery/Photos.'

export class EncounterPhotoOperationError extends Error {
  description: string | string[]

  constructor(description: string | string[]) {
    super(Array.isArray(description) ? description.join(' ') : description)
    this.name = 'EncounterPhotoOperationError'
    this.description = description
  }
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const toRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  return value as Record<string, unknown>
}

const mergeDescriptions = (messages: string[]): string | string[] | null => {
  const uniqueMessages = Array.from(new Set(messages))

  if (uniqueMessages.length === 0) {
    return null
  }

  if (uniqueMessages.length === 1) {
    return uniqueMessages[0]
  }

  return uniqueMessages
}

const normalizeDescription = (value: unknown): string | string[] | null => {
  if (Array.isArray(value)) {
    const messages = value.flatMap((item) => {
      const normalizedItem = normalizeDescription(item)

      if (!normalizedItem) {
        return []
      }

      return Array.isArray(normalizedItem) ? normalizedItem : [normalizedItem]
    })

    return mergeDescriptions(messages)
  }

  if (isNonEmptyString(value)) {
    return value
  }

  const valueRecord = toRecord(value)
  if (!valueRecord) {
    return null
  }

  return (
    normalizeDescription(valueRecord.description) ||
    normalizeDescription(valueRecord.message) ||
    normalizeDescription(valueRecord.error) ||
    normalizeDescription(valueRecord.data)
  )
}

const waitForRetryDelay = (attempt: number): Promise<void> => {
  const timeoutMs = Math.min(
    PHOTO_UPDATE_MAX_BACKOFF_MS,
    PHOTO_UPDATE_INITIAL_BACKOFF_MS * 2 ** Math.max(attempt - 1, 0),
  )

  return new Promise((resolve) => {
    setTimeout(resolve, timeoutMs)
  })
}

const toNormalizedMessages = (value: unknown): string[] => {
  const description = normalizeDescription(value)

  if (!description) {
    return []
  }

  return (Array.isArray(description) ? description : [description]).map((message) =>
    message.toLowerCase(),
  )
}

export const getEncounterPhotoSelectionReadErrorMessage = (): string =>
  ENCOUNTER_PHOTO_SELECTION_READ_ERROR_MESSAGE

export const isEncounterPhotoSelectionReadError = (error: unknown): boolean => {
  const errorRecord = toRecord(error)
  const name = isNonEmptyString(errorRecord?.name) ? errorRecord.name.toLowerCase() : ''
  const message = isNonEmptyString(errorRecord?.message) ? errorRecord.message.toLowerCase() : ''
  const messages = [
    name,
    message,
    ...toNormalizedMessages(errorRecord?.description),
    ...toNormalizedMessages(errorRecord?.data),
  ]

  return messages.some(
    (entry) =>
      entry.includes('err_upload_file_changed') ||
      entry.includes('notreadableerror') ||
      entry.includes('requested file could not be read'),
  )
}

const readFileViaFileReader = (file: File): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result)
      } else {
        reject(new Error('FileReader did not return ArrayBuffer'))
      }
    }

    reader.onerror = () => {
      reject(reader.error ?? new Error('FileReader failed'))
    }

    reader.readAsArrayBuffer(file)
  })

const CANVAS_SNAPSHOT_QUALITY = 0.95

const readFileViaFetchBlobUrl = async (file: File): Promise<ArrayBuffer> => {
  const blobUrl = URL.createObjectURL(file)

  try {
    const response = await fetch(blobUrl)
    const blob = await response.blob()
    // Force Chrome to materialize the blob bytes into JS heap RIGHT NOW,
    // while the content:// URI is still valid. Without this, response.blob()
    // on Android can return a lazy Blob still backed by the picker URI,
    // which then fails later in FormData serialization with ERR_UPLOAD_FILE_CHANGED.
    return await blob.arrayBuffer()
  } finally {
    URL.revokeObjectURL(blobUrl)
  }
}

const readFileViaCanvas = (file: File): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl)
    }

    img.onload = () => {
      cleanup()

      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight

        if (img.naturalWidth === 0 || img.naturalHeight === 0) {
          reject(new Error('Image has zero dimensions'))
          return
        }

        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Canvas 2D context unavailable'))
          return
        }

        ctx.drawImage(img, 0, 0)
        canvas.toBlob(
          (blob) => {
            // Release GPU/CPU pixel buffer eagerly to reduce memory pressure
            // on Android tablets where multiple photos are snapshotted in parallel.
            canvas.width = 0
            canvas.height = 0

            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Canvas toBlob returned null'))
            }
          },
          file.type || 'image/jpeg',
          CANVAS_SNAPSHOT_QUALITY,
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      cleanup()
      reject(new Error('Image failed to load from file'))
    }

    img.src = objectUrl
  })

const buildSnapshotFile = (data: ArrayBuffer | Blob, source: File): File =>
  new File([data], source.name, {
    type: source.type || 'image/jpeg',
    lastModified: source.lastModified,
  })

export const snapshotEncounterPhotoFile = async (file: File): Promise<File> => {
  // Strategy 1: file.arrayBuffer() — fastest, preserves original bytes exactly
  try {
    const bytes = await file.arrayBuffer()
    return buildSnapshotFile(bytes, file)
  } catch {
    // Fall through — any error here (NotReadableError, TypeError, etc.) should
    // trigger the next I/O strategy, not abort the chain.
  }

  // Strategy 2: FileReader API — older callback-based API, different browser I/O path
  try {
    const bytes = await readFileViaFileReader(file)
    return buildSnapshotFile(bytes, file)
  } catch {
    // Fall through to next strategy
  }

  // Strategy 3: fetch() via blob URL — uses Chrome's network stack with forced
  // heap materialization via blob.arrayBuffer() to prevent ERR_UPLOAD_FILE_CHANGED.
  // Can throw TypeError: Failed to fetch on stale content:// URIs — fall through.
  try {
    const bytes = await readFileViaFetchBlobUrl(file)
    return buildSnapshotFile(bytes, file)
  } catch {
    // Fall through to next strategy
  }

  // Strategy 4: Canvas render — loads image via <img>, re-creates pixel data on canvas.
  // Uses the browser's image rendering pipeline, a genuinely different path from the
  // File API. Last resort before giving up.
  try {
    const blob = await readFileViaCanvas(file)
    return buildSnapshotFile(blob, file)
  } catch {
    // All strategies exhausted — fall through to terminal error below
  }

  throw new EncounterPhotoOperationError(getEncounterPhotoSelectionReadErrorMessage())
}

export const buildEncounterPhotoUploadQueue = ({
  category,
  files,
  existingPhotos,
  overrideFileName,
}: BuildEncounterPhotoUploadQueueArgs): PreparedEncounterPhotoUpload[] => {
  const accumulatedExistingPhotos = [...existingPhotos]

  return files.map((file, order) => {
    const fileName =
      overrideFileName && order === 0
        ? overrideFileName
        : generateEncounterPhotoFileName(category, accumulatedExistingPhotos, file.name)

    accumulatedExistingPhotos.push({
      id: -(order + 1),
      filename: fileName,
      mimetype: file.type || 'image/jpeg',
      upload_date: '',
      size: file.size,
      category,
    })

    return {
      file,
      fileName,
      order,
      sourceFileName: file.name,
    }
  })
}

export const downloadEncounterPhotoLocally = (file: File): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const objectUrl = window.URL.createObjectURL(file)
  const downloadLink = document.createElement('a')

  downloadLink.href = objectUrl
  downloadLink.download = file.name
  downloadLink.style.display = 'none'

  document.body.appendChild(downloadLink)
  downloadLink.click()
  downloadLink.remove()

  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl)
  }, 0)
}

export const getEncounterPhotoErrorDescription = (
  error: unknown,
  fallbackMessage: string,
): string | string[] => {
  if (error instanceof EncounterPhotoOperationError) {
    return error.description
  }

  if (isEncounterPhotoSelectionReadError(error)) {
    return getEncounterPhotoSelectionReadErrorMessage()
  }

  const errorRecord = toRecord(error)

  return (
    normalizeDescription(errorRecord?.description) ||
    normalizeDescription(errorRecord?.data) ||
    normalizeDescription(errorRecord?.message) ||
    fallbackMessage
  )
}

export const showPersistentPhotoError = (
  setNotification: NotificationSetter,
  title: string,
  error: unknown,
  fallbackMessage: string,
): void => {
  setNotification({
    type: ALERT_CONSTANTS.ERROR,
    duration: 2,
    title,
    description: getEncounterPhotoErrorDescription(error, fallbackMessage),
  })
}

export const getEncounterPhotoUpdateFailureDescription = (
  response: UpdateEncounterPhotosResponse,
  fallbackMessage: string,
): string | string[] | null => {
  const failureMessages =
    response.results?.flatMap((result) => {
      if (result.success !== false) {
        return []
      }

      const normalizedMessage = normalizeDescription(result.message)

      if (!normalizedMessage) {
        return [fallbackMessage]
      }

      return Array.isArray(normalizedMessage) ? normalizedMessage : [normalizedMessage]
    }) || []

  return mergeDescriptions(failureMessages)
}

export const isEncounterPhotoFileTooLarge = (file: File): boolean =>
  file.size > MAX_ENCOUNTER_PHOTO_FILE_SIZE_BYTES

export const getEncounterPhotoFileTooLargeMessage = (): string => ENCOUNTER_PHOTO_FILE_SIZE_MESSAGE

export const createEncounterPhotoUploadFailureError = (
  failures: EncounterPhotoUploadFailure[],
  fallbackMessage: string,
): EncounterPhotoOperationError =>
  new EncounterPhotoOperationError(
    failures.length === 0
      ? fallbackMessage
      : failures
          .map((failure) => {
            const description = Array.isArray(failure.description)
              ? failure.description.join(' ')
              : failure.description
            return `${failure.sourceFileName}: ${description}`
          })
          .join(' '),
  )

export const assertEncounterPhotoUpdateSucceeded = (
  response: UpdateEncounterPhotosResponse,
  fallbackMessage: string,
): void => {
  const failureDescription = getEncounterPhotoUpdateFailureDescription(response, fallbackMessage)

  if (!failureDescription) {
    return
  }

  throw new EncounterPhotoOperationError(failureDescription)
}

export const retryEncounterPhotoUpdates = async ({
  execute,
  files,
  fallbackMessage,
}: RetryEncounterPhotoUpdatesArgs): Promise<UpdateEncounterPhotosResponse> => {
  let pendingFiles = [...files]
  let lastResponse: UpdateEncounterPhotosResponse | null = null

  for (let attempt = 1; attempt <= PHOTO_UPDATE_MAX_RETRIES; attempt += 1) {
    lastResponse = await execute(pendingFiles)

    const failedFileIds = new Set(
      (lastResponse.results || [])
        .filter((result) => result.success === false)
        .map((result) => result.file_id),
    )

    if (failedFileIds.size === 0) {
      return lastResponse
    }

    pendingFiles = pendingFiles.filter((file) => failedFileIds.has(file.file_id))

    if (pendingFiles.length === 0) {
      return lastResponse
    }

    if (attempt === PHOTO_UPDATE_MAX_RETRIES) {
      break
    }

    await waitForRetryDelay(attempt)
  }

  throw new EncounterPhotoOperationError(
    getEncounterPhotoUpdateFailureDescription(
      lastResponse || {
        statusCode: 0,
        success: false,
        message: fallbackMessage,
        results: [],
      },
      fallbackMessage,
    ) || fallbackMessage,
  )
}
